import type { QueryClient } from '@tanstack/react-query'
import { Spinner } from '@/components/ui/spinner'

import { isAuthenticated } from '@/utils/auth'
import { paths } from '@/configs/paths'
import { redirect, type LoaderFunctionArgs } from 'react-router-dom'
import type { RouteModule } from '@/types/router'

/**
 * Converts a lazy-loaded route module into a format compatible with React Router.
 * It injects the QueryClient into `clientLoader` and `clientAction` functions,
 * allowing data loading functions to be co-located with their respective pages.
 * @param queryClient The TanStack Query client instance.
 * @returns A function that processes the imported module.
 */
export const convertToRouteObject = (queryClient: QueryClient) => (module: RouteModule) => {
  const { clientLoader, clientAction, default: Component, ...rest } = module
  return {
    ...rest,
    loader: clientLoader
      ? async (args: LoaderFunctionArgs) => {
          if (!isAuthenticated()) {
            const url = new URL(args.request.url)
            const redirectTo = url.pathname + url.search
            throw redirect(paths.login.getHref(redirectTo))
          }
          return clientLoader(queryClient)(args)
        }
      : undefined,
    action: clientAction?.(queryClient),
    loadingComponent: Spinner,
    Component
  }
}
